import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// --- Image utilities ---
// Fetch remote image as ArrayBuffer
const fetchImageBuffer = async (url) => {
  const fullUrl = url.startsWith("http")
    ? url
    : `https://regulatoryreformfiresafety.seitechinternational.com${url}`;
  const res = await fetch(fullUrl);
  if (!res.ok) throw new Error("Failed to fetch image: " + url);
  return await res.arrayBuffer();
};

// Compress and resize local image (File object)
const compressImage = async (file) => {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = new window.Image();
  img.src = dataUrl;
  await new Promise((res) => (img.onload = res));
  const canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 200;
  canvas.getContext("2d").drawImage(img, 0, 0, 200, 200);
  const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.7);
  const response = await fetch(resizedDataUrl);
  return await response.arrayBuffer();
};

// Check if need a new PDF page for content, add one if so
const checkPageSpace = (doc, y, needed, margin, pageHeight) => {
  if (y + needed > pageHeight - margin) {
    doc.addPage();
    return margin;
  }
  return y;
};

// PDF cell color for action plan risk
const getPdfRiskColor = (level = "") => {
  switch ((level || "").toLowerCase()) {
    case "intolerable":
    case "substantial":
    case "high":
      return [[211, 47, 47], [255, 255, 255]]; // red, white
    case "moderate":
      return [[255, 152, 0], [255, 255, 255]]; // orange, white
    case "tolerable":
      return [[255, 253, 231], [68, 68, 68]]; // yellow, dark
    case "trivial":
    case "low":
      return [[165, 214, 167], [34, 34, 34]]; // green, dark
    default:
      return [null, null];
  }
};

// --- FIELD MAPPING HELPERS ---
function getHeaderField(header = {}, key, fallback = "") {
  return header?.[key] || fallback;
}
function getExecutiveSummaryField(executiveSummary = {}, key, fallback = "") {
  return executiveSummary?.[key] || fallback;
}
function getOccupantNumber(occupants = {}, key) {
  return occupants?.[key] !== undefined && occupants?.[key] !== "" ? occupants[key] : "Not Provided";
}

// --- MAIN PDF EXPORT ---
export const generateFireRiskPdf = async (data = {}, questions = {}) => {
  const {
    fireHazardsQuestions = [],
    fireProtectionQuestions = [],
    managementQuestions = [],
  } = questions;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  const lineSpacing = 10;
  let y = margin;

  // --- Helpers for text and tables ---
  const addText = (text, size = 12, bold = false, align = "left", color = "000000", maxWidth = pageWidth - 2 * margin) => {
    doc.setFontSize(size);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setTextColor("#" + color);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, align === "center" ? pageWidth / 2 : margin, y, { align });
    y = checkPageSpace(doc, y + lines.length * (size * 0.5), lineSpacing, margin, pageHeight);
    y += lines.length * (size * 0.5);
  };

  const addSectionTitle = (title) => {
    y = checkPageSpace(doc, y, 20, margin, pageHeight);
    addText(title, 14, true, "left", "236B50");
    y += 5;
  };

  const addTable = (headers, rows, colWidths = null, tableOpts = {}) => {
    y = checkPageSpace(doc, y, 50, margin, pageHeight);
    autoTable(doc, {
      startY: y,
      head: [headers],
      body: rows,
      columnStyles: colWidths ? Object.fromEntries(headers.map((_, i) => [i, { cellWidth: colWidths[i] }])) : {},
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontSize: 8 },
      margin: { left: margin, right: margin },
      ...tableOpts
    });
    y = doc.lastAutoTable.finalY + lineSpacing;
  };

  // ---- COVER PAGE ----
  addText("FIRE RISK ASSESSMENT", 24, true, "center", "236B50");
  addText(getHeaderField(data.header, "auditTitle", "Assessment Title Not Provided"), 14, false, "center");
  addText(getHeaderField(data.header, "siteAddress", "Address Not Provided"), 14, false, "center");
  addText(`Ref: ${getHeaderField(data.header, "auditNumber", "FRA-SEI-06072025-001")}`, 14, true, "center");
  y += 10;
  addText(getHeaderField(data.header, "dateConducted", new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })), 14, false, "center");
  y += 20;
  addText(`Dear ${getHeaderField(data.header, "client", "Client")}`, 14, false, "left");
  addText("Fire Risk Assessment", 16, true, "left");
  addText("The Regulatory Reform (Fire Safety) Order 2005", 14, true, "left");
  addText(
    getHeaderField(data.header, "siteAddress", "") ? getHeaderField(data.header, "siteAddress", "") : "Property Details Not Provided",
    14, false, "left"
  );
  addText(`Completed on: ${getHeaderField(data.header, "dateConducted", "Date Not Provided")}`, 14, false, "left");
  y += 10;
  addText(
    "Please find attached the completed Fire Risk Assessment for the premises, conducted in accordance with the Regulatory Reform (Fire Safety) Order 2005, detailing identified fire hazards, protection measures, management procedures, and recommended actions to ensure compliance and enhance safety.",
    12, false, "left"
  );
  y += 20;
  addText("Yours sincerely,", 12, false, "left");
  addText(getHeaderField(data.header, "assessor", "Assessor Name Not Provided"), 12, true, "left");
  addText(getHeaderField(data.header, "assessorTitle", "Principal OHSE Consultant"), 12, false, "left");
  addText("Fire Risk, Training & Compliance Specialist", 12, false, "left");
  addText(getHeaderField(data.header, "assessorQualifications", "Qualifications Not Provided"), 12, false, "left");
  y += 10;
  addText("Please Note:", 12, true, "left");
  addText(
    "This assessment report is intended to assist you in improving compliance with the Regulatory Reform (Fire Safety) Order 2005 by identifying potential fire hazards, evaluating existing safety measures, and providing actionable recommendations to mitigate risks and ensure the safety of occupants and premises.",
    10, false, "left"
  );
  doc.addPage();
  y = margin;

  // ---- HEADER TABLE / SUMMARY ----
  addSectionTitle("ASSESSMENT SUMMARY");
  const headerData = [
    ["Assessment Title", getHeaderField(data.header, "auditTitle", "")],
    ["Reference Number", getHeaderField(data.header, "auditNumber", "")],
    ["Date Conducted", getHeaderField(data.header, "dateConducted", "")],
    ["Assessor", getHeaderField(data.header, "assessor", "")],
    ["Reviewer/Validator", getHeaderField(data.header, "reviewer", "")],
    ["Client/Responsible Person", getHeaderField(data.header, "client", "")],
    ["Site Address", getHeaderField(data.header, "siteAddress", "")],
    ["Property Details", getHeaderField(data.header, "PropertyDetails", "")]
  ];
  addTable(["Field", "Value"], headerData, [60, 130]);

  // ---- EXECUTIVE SUMMARY ----
  addSectionTitle("EXECUTIVE SUMMARY");
  const execSummaryData = [
    ["Purpose", getExecutiveSummaryField(data.executiveSummary, "purpose", "")],
    ["Scope", getExecutiveSummaryField(data.executiveSummary, "scope", "")],
    ["Strategy", getExecutiveSummaryField(data.executiveSummary, "strategy", "")],
    ["Fire History", getExecutiveSummaryField(data.executiveSummary, "fireHistory", "")],
    ["Life Safety Concerns", getExecutiveSummaryField(data.executiveSummary, "lifeSafety", "")],
    ["Property Concerns", getExecutiveSummaryField(data.executiveSummary, "property", "")],
    ["Business Concerns", getExecutiveSummaryField(data.executiveSummary, "business", "")],
    ["Environmental Concerns", getExecutiveSummaryField(data.executiveSummary, "environmental", "")],
    ["Conclusion", getExecutiveSummaryField(data.executiveSummary, "conclusion", "")]
  ];
  addTable(["Item", "Summary"], execSummaryData, [60, 130]);

  // ---- OCCUPANTS SECTION ----
  addSectionTitle("OCCUPANTS & PERSONS AT RISK");
  const occ = data.occupants || {};
  const occTable = [
    ["General Occupants", getOccupantNumber(occ, "general")],
    ["Sleeping Occupants", getOccupantNumber(occ, "sleeping")],
    ["Persons with Disabilities", getOccupantNumber(occ, "disabled")],
    ["Young Persons", getOccupantNumber(occ, "young")],
    ["Visitors", getOccupantNumber(occ, "visitors")],
    ["Other", getOccupantNumber(occ, "other")]
  ];
  addTable(["Type", "Number"], occTable, [60, 130]);

  // ---- FIRE LOSS EXPERIENCE ----
  addSectionTitle("FIRE LOSS EXPERIENCE");
  if (Array.isArray(data.fireLosses) && data.fireLosses.length > 0 && data.fireLosses.some(l => l.date || l.nature || l.consequences)) {
    addTable(
      ["Date", "Nature", "Consequences"],
      data.fireLosses.map(loss => [
        loss.date || "-",
        loss.nature || "-",
        loss.consequences || "-"
      ]),
      [40, 70, 80]
    );
  } else {
    addText("No fire loss experience recorded.", 10, false);
  }

  // ---- LEGISLATION ----
  addSectionTitle("RELEVANT LEGISLATION & STANDARDS");
  if (Array.isArray(data.legislation) && data.legislation.length > 0) {
    addTable(["Applicable Legislation"], data.legislation.map(l => [l]), [190]);
  } else {
    addText("No legislation provided.", 10, false);
  }

  // ---- CHECKLIST SECTIONS ----
  const checklistSection = (title, questions, answers) => {
    addSectionTitle(title);
    questions.forEach(q => {
      addText(q.text, 11, true);
      addTable(
        ["Answer", "Deficiencies / Comments"],
        [
          [
            (answers[q.key]?.answer || "-"),
            (answers[q.key]?.deficiencies || answers[q.key]?.comments || "")
          ]
        ],
        [40, 150]
      );
    });
  };

  if (data.fireHazards) checklistSection("FIRE HAZARDS", fireHazardsQuestions, data.fireHazards);
  if (data.fireProtection) checklistSection("FIRE PROTECTION", fireProtectionQuestions, data.fireProtection);
  if (data.management) checklistSection("FIRE SAFETY MANAGEMENT", managementQuestions, data.management);

  // ---- ACTION PLAN (with color!) ----
  addSectionTitle("ACTION PLAN");
  if (Array.isArray(data.actionPlan) && data.actionPlan.length > 0) {
    const colPriority = 0, colSeverity = 1;
    autoTable(doc, {
      startY: y,
      head: [["Priority", "Severity", "Issue", "Allocation", "Time Scale", "Responsible"]],
      body: data.actionPlan.map(item => [
        item.priority || "",
        item.severity || "",
        item.issue || "",
        item.allocation || "",
        item.timeScale || "",
        item.responsible || ""
      ]),
      columnStyles: {
        0: { cellWidth: 22 }, // Priority
        1: { cellWidth: 22 }, // Severity
        2: { cellWidth: 54 }, // Issue
        3: { cellWidth: 30 },
        4: { cellWidth: 32 },
        5: { cellWidth: 30 }
      },
      styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [35, 107, 80], textColor: [255, 255, 255], fontSize: 10 },
      margin: { left: margin, right: margin },
      didParseCell: function (tableData) {
        if (tableData.section === "body" && (tableData.column.index === colPriority || tableData.column.index === colSeverity)) {
          const value = tableData.cell.raw || "";
          const [bg, text] = getPdfRiskColor(value);
          if (bg) tableData.cell.styles.fillColor = bg;
          if (text) tableData.cell.styles.textColor = text;
          tableData.cell.styles.fontStyle = "bold";
        }
      }
    });
    y = doc.lastAutoTable.finalY + lineSpacing;

    // Color legend
    addText("Risk/Warning Color Legend:", 11, true);
    [
      "Red = Intolerable / Substantial / High: Immediate action required. Do not occupy until risk is reduced.",
      "Orange = Moderate: Act to reduce risk in a defined timescale.",
      "Yellow = Tolerable: No major controls needed, consider improvements.",
      "Green = Trivial / Low: No action required, risk acceptable."
    ].forEach(str => addText(str, 9, false));
  } else {
    addText("No action plan items recorded.", 10, false);
  }

  // ---- REVIEW / FOLLOW-UP ----
  addSectionTitle("REVIEW / FOLLOW-UP");
  const review = data.review || {};
  addTable(
    ["Review Date", "Reason", "Follow Up"],
    [[
      review.date || "Not Provided",
      review.reason || "Not Provided",
      review.followUp || "Not Provided"
    ]],
    [40, 70, 80]
  );

  // ---- SIGNATURES ----
  addSectionTitle("SIGNATURES");
  const sig = data.signatures || {};
  addTable(
    ["Assessor Name", "Assessor Signature", "Assessor Date", "Validator Name", "Validator Signature", "Validator Date", "Client Name", "Client Signature", "Client Date"],
    [[
      sig.assessorName || "",
      sig.assessorSignature ? "✔" : "",
      sig.assessorDate || "",
      sig.validatorName || "",
      sig.validatorSignature ? "✔" : "",
      sig.validatorDate || "",
      sig.clientName || "",
      sig.clientSignature ? "✔" : "",
      sig.clientDate || ""
    ]],
    [24, 16, 18, 22, 16, 18, 22, 16, 18]
  );

  // ---- PHOTO EVIDENCE / ATTACHMENTS (local & remote support) ----
  addSectionTitle("PHOTO EVIDENCE / ATTACHMENTS");
  if (Array.isArray(data.evidenceFiles) && data.evidenceFiles.length > 0) {
    for (const evidence of data.evidenceFiles) {
      y = checkPageSpace(doc, y, 60, margin, pageHeight);
      try {
        let arrayBuffer = null;
        if (evidence.file) {
          arrayBuffer = await compressImage(evidence.file); // local file
        } else if (evidence.url) {
          arrayBuffer = await fetchImageBuffer(evidence.url); // remote image
        }
        if (arrayBuffer) {
          doc.addImage(arrayBuffer, "JPEG", margin, y, 50, 50);
          y += 55;
          addText(`Evidence: ${evidence.name || evidence.file?.name || evidence.url?.split("/").pop()}`, 10, false);
        }
      } catch (error) {
        console.warn("Failed to add image:", error);
        addText("Failed to render evidence image", 10, false);
      }
    }
  } else {
    addText("No general photo evidence/attachments included.", 10, false);
  }

  // ---- SAVE PDF ----
  const fileName = `Fire_Risk_Assessment_${getHeaderField(data.header, "siteAddress", "Report")}.pdf`.replace(/[^a-z0-9]/gi, "_");
  doc.save(fileName);
};
